import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* ================= helpers (shared) ================= */

const pad2 = (n) => String(n).padStart(2, "0");

const formatDMY = (dLike) => {
  const d = new Date(dLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const format12h = (d) => {
  if (!d) return "";
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};

// decimal hours (e.g., 19.5) -> "HH:MM"
const decToHHMM = (decimalHrs) => {
  if (decimalHrs === null || decimalHrs === undefined || decimalHrs === "") return "";
  const n = Number(decimalHrs);
  if (Number.isNaN(n)) return String(decimalHrs);
  const hh = Math.floor(n);
  const mm = Math.round((n - hh) * 60);
  return `${pad2(hh)}:${pad2(mm)}`;
};

// decimal hours -> AM/PM time-of-day (visual only)
const decToAMPM = (decimalHrs) => {
  const hhmm = decToHHMM(decimalHrs);
  if (!hhmm.includes(":")) return "";
  const [hh, mm] = hhmm.split(":").map((x) => parseInt(x, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return "";
  const t = new Date();
  t.setHours(hh, mm, 0, 0);
  return format12h(t);
};

// Working Hours renderer: <24h => "x.y hours", >=24h => "Xd Y.h"
const formatWorkingTotal = (hoursDecimal) => {
  if (hoursDecimal === null || hoursDecimal === undefined || hoursDecimal === "") return "";
  const n = Number(hoursDecimal);
  if (Number.isNaN(n)) return String(hoursDecimal);

  if (n < 24) {
    const s = Number.isInteger(n) ? `${n}` : `${parseFloat(n.toFixed(1))}`;
    return `${s} hours`;
  }
  const days = Math.floor(n / 24);
  const rem = n - days * 24;
  if (Math.abs(rem) < 1e-6) return `${days} day${days > 1 ? "s" : ""}`;
  const remStr = Number.isInteger(rem) ? `${rem}` : `${parseFloat(rem.toFixed(1))}`;
  return `${days} day${days > 1 ? "s" : ""} ${remStr}h`;
};

// tiny inline UI bits
const chipStyle = (highlight) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.6,
  background: highlight ? "#fff7ed" : "#eef2ff",
  color: highlight ? "#9a3412" : "#3730a3",
});
const cellStack = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
};
/* =================================================== */

const OwnVehiclePostion = () => {
  useAuthRedirect();
  const { id, branch } = useParams();
  const [data, setData] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState("");
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const startYear = 2007;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [cum, setCum] = useState("");

  const canView = hasPermission("Vehicle Log Book", "view");

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://rfs2.infraconsoft.net.in/api/log_book_postion_own", {
        params: { month: selectedMonth, year: selectedYear, id },
      });

      const responseData = response.data.postionData || [];
      setData(responseData);
      setFilteredData(responseData);

      if (responseData.length > 0 && responseData[0].fld_vbasis) {
        setVehicleBasis(responseData[0].fld_vbasis);
      }
    } catch (error) {
      console.error("Error fetching postionData:", error);
    } finally {
      setLoading(false);
    }
  };

  function getCumulativeDate(selectedMonth, selectedYear) {
    let cumMonth, cumSelectYear, cumDay;

    if (selectedMonth === 1) {
      cumMonth = 12;
      cumSelectYear = selectedYear - 1;
    } else {
      cumMonth = selectedMonth - 1;
      cumSelectYear = selectedYear;
    }

    switch (cumMonth) {
      case 1: case 3: case 5: case 7: case 8: case 10: case 12: cumDay = 31; break;
      case 4: case 6: case 9: case 11: cumDay = 30; break;
      case 2:
        cumDay = (cumSelectYear % 4 === 0 && (cumSelectYear % 100 !== 0 || cumSelectYear % 400 === 0)) ? 29 : 28;
        break;
      default: cumDay = 0;
    }

    const paddedMonth = cumMonth.toString().padStart(2, "0");
    const paddedDay = cumDay.toString().padStart(2, "0");
    return `${cumSelectYear}-${paddedMonth}-${paddedDay}`;
  }

  useEffect(() => {
    const fetchCumTot = async () => {
      const cumDate = getCumulativeDate(selectedMonth, selectedYear);

      try {
        const response = await axios.get("https://rfs2.infraconsoft.net.in/api/log_book_get_cum_own", {
          params: { vcode: id, date: cumDate },
        });

        if (response.data && response.data.cumulativeHrs?.length > 0) {
          const item = response.data.cumulativeHrs[0];

          const cumulativeHours = item.cumulativehours ?? 0;
          const cumulativeKm = item.cumulativekm ?? 0;
          const vehicleBasis = item.vehicle_basis ?? "";
          const existingCumulative = item.cumulative ?? 0;

          let total = 0;
          if (vehicleBasis === "Km") {
            total = existingCumulative + cumulativeKm;
          } else {
            total = existingCumulative + cumulativeHours;
          }

          setCum({
            vehicleBasis,
            cumulativeKm,
            cumulativeHours,
            existingCumulative,
            total,
          });
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };

    fetchCumTot();
  }, [id, selectedMonth, selectedYear]);

  const handleSearch = (e) => {
    const term = (e.target.value || "").toLowerCase();
    setSearchTerm(term);

    if (!term) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter(
      (item) =>
        String(item.stn_purnumber || "").toLowerCase().includes(term) ||
        String(item.stn_dpt || "").toLowerCase().includes(term)
    );

    setFilteredData(filtered);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // near top of component
const [site, setSite] = useState({
  ok: true,
  name: "Infraconsoft",
  logo: null,
  address: "",
  email: "",
  phone: "",
  _ts: 0,
});

useEffect(() => {
  let ignore = false;
  (async () => {
    try {
      const res = await axios.get("https://rfs2.infraconsoft.net.in/api/siteinfo");
      const s = res?.data || {};
      let logo = s.logo || null;
      if (logo && !/^https?:\/\//i.test(logo)) {
        if (!logo.startsWith("/")) logo = `/${logo}`;
        logo = `${window.location.origin}${logo}`;
      }
      if (!ignore) {
        setSite({
          ok: !!s.ok,
          name: s.name || "Infraconsoft",
          logo,
          address: s.address || "",
          email: s.email || "",
          phone: s.phone || "",
          _ts: Date.now(),
        });
      }
    } catch (e) {
      console.error("Failed to load /siteinfo", e);
    }
  })();
  return () => { ignore = true; };
}, []);


  /* ================= Excel: AM/PM + formatted working hours; running cumulative like table ================= */
  const exportToExcel = () => {
  // Running totals for the current page (match the table)
  let Totalworking = 0;   // hours running sum for the page
  let TotalworkingKm = 0; // km running sum for the page

  // Build row objects in your same format & order
  const rows = currentData.map((item, index) => {
    const workHr = parseFloat(item.fld_wohrs || 0);
    const endkm = parseFloat(item.fld_endkm || 0);
    const stkm = parseFloat(item.fld_stkm || 0);
    const wkm = endkm - stkm;

    // running sums (same as table; no undefined cum?.total)
    Totalworking += workHr;
    TotalworkingKm += wkm;

    const cumHourDisplay = Totalworking.toFixed(2);
    const cumKMDisplay = TotalworkingKm.toFixed(2);

    const dateStr = formatDMY(item.fld_vdate);
    const startAMPM = decToAMPM(item.fld_sthrs);
    const endAMPM = decToAMPM(item.fld_endhrs);
    const workingPretty = formatWorkingTotal(workHr);

    const baseData = {
      "S.No": index + 1,
      "Branch": item.fld_branch,
      "Date": dateStr,
      "Starting Time": startAMPM,    // formatted AM/PM
      "Closing Time": endAMPM,       // formatted AM/PM
      "Starting KM": stkm.toFixed(2),
      "Closing KM": endkm.toFixed(2),
      "No Of Loads": item.fld_noload,
      "Location": item.fld_location,
    };

    if (vehicleBasis === "Hour") {
      return {
        ...baseData,
        "Working Hours": workingPretty,   // formatted
        "Cumulative Hours": cumHourDisplay,
      };
    } else if (vehicleBasis === "Km") {
      return {
        ...baseData,
        "Working KM": wkm.toFixed(2),
        "Cumulative KM": cumKMDisplay,
      };
    } else {
      return baseData;
    }
  });

  // Derive headers from first row (preserves your order)
  const headers = rows.length ? Object.keys(rows[0]) : [
    "S.No","Branch","Date","Starting Time","Closing Time","Starting KM","Closing KM",
    "Working KM","Cumulative KM","No Of Loads","Location"
  ];

  // Convert row objects -> AOA
  const dataRows = rows.map(r => headers.map(h => r[h] ?? ""));

  // Build company header + title
  const COLS = headers.length;
  const fullWidth = (text = "") => [text, ...Array(COLS - 1).fill("")];
  const title = `Own Vehicle Log Book — ${String(selectedMonth).padStart(2,"0")}/${selectedYear}`;

  const aoa = [
    fullWidth((site.name || "Infraconsoft").toUpperCase()),
    fullWidth(site.address || ""),
    fullWidth(
      [site.phone ? `Ph: ${site.phone}` : "", site.email ? `Email: ${site.email}` : ""]
        .filter(Boolean)
        .join(" | ")
    ),
    [], // spacer
    fullWidth(title),
    [], // spacer
    headers,
    ...dataRows,
  ];

  // Create worksheet from AOA (so we can prepend header rows)
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  // Merge header lines across all columns
  worksheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } }, // company name
    { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } }, // address
    { s: { r: 2, c: 0 }, e: { r: 2, c: COLS - 1 } }, // phone/email
    { s: { r: 4, c: 0 }, e: { r: 4, c: COLS - 1 } }, // title
  ];

  // Column widths — keep your sizing where it makes sense
  const widthMap = {
    "S.No": 6,
    "Branch": 20,
    "Date": 12,
    "Starting Time": 16,
    "Closing Time": 16,
    "Starting KM": 12,
    "Closing KM": 12,
    "Working Hours": 16,
    "Cumulative Hours": 18,
    "Working KM": 16,
    "Cumulative KM": 18,
    "No Of Loads": 14,
    "Location": 30,
  };
  worksheet["!cols"] = headers.map(h => ({ wch: widthMap[h] || 14 }));

  // Freeze after the header block (0..5 are header/spacer rows; row 6 is table header)
  worksheet["!freeze"] = { xSplit: 0, ySplit: 6, topLeftCell: "A7", activePane: "bottomLeft" };

  // Workbook + save
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Own Vehicle Log Book");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
  });
  saveAs(blob, `own_veh_log_book_${selectedMonth}-${selectedYear}.xlsx`);
};
/* ======================================================================================================== */

  if (!canView) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Own Vehicle Position - History</h2>
            <Link to={`/vehicle_logbook_list`}>
              <p className="back-button"><i className="fa-solid fa-backward"></i> Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="add-new-container">
          <button className="add-new-btn" onClick={exportToExcel}>
            <i className="fa-solid fa-file-excel"></i> Export to Excel
          </button>
        </div>

        <div className="month-year-container">
          <p className="title">Select your Month & Year:</p>
          <div className="select-container">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="custom-select"
            >
              {[...Array(12).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>{pad2(i + 1)}</option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="custom-select"
            >
              {[...Array(currentYear - startYear + 1).keys()].map((i) => (
                <option key={startYear + i} value={startYear + i}>{startYear + i}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Branch</th>
                <th>Date</th>
                {vehicleBasis === "Hour" && <th>Starting Hours</th>}
                {vehicleBasis === "Hour" && <th>Closing Hours</th>}
                {vehicleBasis === "Hour" && <th>Working Hours</th>}
                {vehicleBasis === "Hour" && <th>Cumulative Hours</th>}
                {vehicleBasis === "Km" && <th>Starting KM</th>}
                {vehicleBasis === "Km" && <th>Closing KM</th>}
                {vehicleBasis === "Km" && <th>Working KM</th>}
                {vehicleBasis === "Km" && <th>Cumulative KM</th>}
                <th>No Of Loads</th>
                <th>Location</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="16" className="loading-text">Loading data, please wait...</td>
                </tr>
              ) : currentData.length > 0 ? (
                (() => {
                  let Totalworking = 0;   // hours running sum for the page
                  let Totalworking1 = 0;  // km running sum for the page
                  let CumHour = 0;
                  let CumKm = 0;
                  let wkm = 0;

                  return currentData.map((item, index) => {
                    const workHr = parseFloat(item.fld_wohrs || 0);
                    const endkm = parseFloat(item.fld_endkm || 0);

                    // hour running totals
                    Totalworking += workHr;
                    CumHour = Totalworking + (cum?.total || 0);

                    // km running totals
                    wkm = (parseFloat(item.fld_endkm || 0) - parseFloat(item.fld_stkm || 0)) || 0;
                    Totalworking1 += wkm;
                    CumKm = Totalworking1 + (cum?.total || 0);

                    const stkmDisplay = parseFloat(item.fld_stkm || 0).toFixed(2);
                    const endkmDisplay = endkm.toFixed(2);
                    const workHrDisplay = workHr.toFixed(2);
                    const cumHourDisplay = CumHour.toFixed(2);
                    const cumKMDisplay = CumKm.toFixed(2);
                    const wkmFormat = wkm.toFixed(2);

                    // AM/PM + chip for Hour basis (highlight chip if >=24h)
                    const dateStr = formatDMY(item.fld_vdate);
                    const isMultiDay = Number(item.fld_wohrs || 0) >= 24;
                    const startAMPM = decToAMPM(item.fld_sthrs);
                    const endAMPM = decToAMPM(item.fld_endhrs);
                    const workingPretty = formatWorkingTotal(workHr);

                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.fld_branch}</td>
                        <td>{new Date(item.fld_vdate).toLocaleDateString("en-GB")}</td>

                        {vehicleBasis === "Hour" && (
                          <>
                            {/* Start column with date chip + bold AM/PM, aligned */}
                            <td style={{ verticalAlign: "top" }}>
                              <div style={cellStack}>
                                <span style={chipStyle(isMultiDay)}>{dateStr}</span>
                                <span style={{ fontWeight: 700 }}>{startAMPM || "—"}</span>
                              </div>
                            </td>

                            {/* End column with same alignment */}
                            <td style={{ verticalAlign: "top" }}>
                              <div style={cellStack}>
                                <span style={chipStyle(isMultiDay)}>{dateStr}</span>
                                <span style={{ fontWeight: 700 }}>{endAMPM || "—"}</span>
                              </div>
                            </td>

                            {/* Working Hours formatted; cumulative numeric */}
                            <td>{workingPretty || "—"}</td>
                            <td>{cumHourDisplay}</td>
                          </>
                        )}

                        {vehicleBasis === "Km" && (
                          <>
                            <td>{stkmDisplay}</td>
                            <td>{endkmDisplay}</td>
                            <td>{wkmFormat}</td>
                            <td>{cumKMDisplay}</td>
                          </>
                        )}

                        <td>{item.fld_noload}</td>
                        <td>{item.fld_location}</td>
                      </tr>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan="16">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>

          <button onClick={() => paginate(1)} className={currentPage === 1 ? 'active' : ''}>
            1
          </button>

          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map((_, index) => {
              const pageNumber = currentPage + index;
              if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? 'active' : ""}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnVehiclePostion;
